import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { getAbonnementStats, listAbonnementen } from "@/features/abonnementen/abonnementen.api";
import { Status } from "@/components/ui/Status";
import { fmtMoney, fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_shell/overzicht")({
  component: OverzichtPage,
});

function OverzichtPage() {
  const { t } = useTranslation();
  const statsQuery = useQuery({ queryKey: ["abonnementen", "stats"], queryFn: getAbonnementStats });
  const recentQuery = useQuery({
    queryKey: ["abonnementen", { pageSize: "10" }],
    queryFn: () => listAbonnementen({ pageSize: "10" }),
  });

  const stats = statsQuery.data;
  const recent = recentQuery.data?.data ?? [];
  const attention = recent.filter((a) => a.status === "verlenging" || a.status === "betaling");
  // Verdeling per productgroep over de factureerbare onderdelen — dat is de
  // dimensie waarop ook geboekt wordt.
  const maxGroep = Math.max(1, ...(stats?.perProductgroep.map((g) => g.aantal) ?? [1]));

  return (
    <div className="app-page" data-screen-label="Backend Overzicht">
      <div className="page-header">
        <div>
          <div className="page-header__title">
            <h1>{t("overzicht.title")}</h1>
          </div>
          <p className="page-header__sub">{t("overzicht.subtitle")}</p>
        </div>
        <div className="page-header__actions">
          <button className="dtw-btn dtw-btn--secondary">
            <Icon name="calendar" size={15} /> Deze maand
          </button>
          <button className="dtw-btn dtw-btn--ghost">
            <Icon name="download" size={15} /> {t("action.export")}
          </button>
        </div>
      </div>

      {/* KPI's */}
      <div className="kpi-grid">
        <Kpi
          icon="refresh-cw"
          label={t("overzicht.kpi.actief")}
          value={fmtNumber(stats?.actief)}
        />
        <Kpi
          icon="euro"
          label={t("overzicht.kpi.maandomzet")}
          value={fmtMoney(stats?.maandOmzetCents)}
        />
        <Kpi icon="clock" label={t("overzicht.kpi.proef")} value={fmtNumber(stats?.proef)} />
        <Kpi
          icon="alert-triangle"
          label={t("overzicht.kpi.aandacht")}
          value={fmtNumber((stats?.terVerlenging ?? 0) + (stats?.wachtOpBetaling ?? 0))}
        />
      </div>

      {/* Onderste rij: vraagt aandacht + verdeling per pakket */}
      <div style={{ display: "grid", gridTemplateColumns: "2.2fr 1fr", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">{t("overzicht.attention.title")}</div>
          </div>
          {attention.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">
                <Icon name="check-circle" size={22} />
              </div>
              <p>{t("overzicht.attention.empty")}</p>
            </div>
          ) : (
            <div className="panel__body flush">
              <div className="dtw-table-wrap">
                <table className="dtw-table">
                  <thead>
                    <tr>
                      <th>{t("abonnementen.col.abonnement")}</th>
                      <th>{t("abonnementen.col.status")}</th>
                      <th className="num">{t("abonnementen.col.maandbedrag")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attention.map((a) => (
                      <tr key={a.id} className="row-link">
                        <td>
                          <Link
                            to="/abonnementen/$id"
                            params={{ id: a.nummer }}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            <div className="dtw-table__cell-stack">
                              <span className="mono">{a.nummer}</span>
                              <span className="t-tertiary">{a.klant.naam}</span>
                            </div>
                          </Link>
                        </td>
                        <td>
                          <Status status={a.status} />
                        </td>
                        <td className="num mono">{fmtMoney(a.maandbedragCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">{t("overzicht.distribution.title")}</div>
          </div>
          <div className="panel__body">
            {(stats?.perProductgroep ?? []).map((g) => (
              <div className="dist-row" key={g.productgroep}>
                <span>{g.label}</span>
                <div className="dist-bar">
                  <div className="dist-bar__fill" style={{ width: `${(g.aantal / maxGroep) * 100}%` }} />
                </div>
                <span className="mono num">{fmtNumber(g.aantal)}</span>
              </div>
            ))}
            {(!stats || stats.perProductgroep.length === 0) && (
              <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
                {t("common.none")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: Parameters<typeof Icon>[0]["name"]; label: string; value: string }) {
  return (
    <div className="kpi">
      <div className="kpi__label">
        <span className="ico">
          <Icon name={icon} size={14} />
        </span>
        {label}
      </div>
      <div className="kpi__value">
        <span className="num">{value}</span>
      </div>
    </div>
  );
}
